import { getApiBaseUrl } from '../services/api';
import { resolveDisplayPhotoUrl } from './photoUrl';

/**
 * Resolve any stored or API-supplied image URL to the URL that should actually be displayed.
 *
 * Relative paths get the configured API base URL prepended, as they always have.
 *
 * S3 URLs are additionally rewritten onto condo-manager's stable image proxy
 * (/api/v1/images/{bucket}/{key}). The backend hands out presigned URLs, and a presigned URL
 * is the wrong thing to put in an <img src>:
 *
 *   1. It expires, and sooner than it looks. GetBoundedPresignedURLFromURL clamps the signature
 *      to the end of the current 15-minute wall-clock bucket rather than to 15 minutes from now
 *      (condo-manager services/s3_service.go, presignWindowForCredentials), so a photo handed
 *      out at 14:59:30 is valid for thirty seconds. After that S3 answers 403 and the photo
 *      breaks with nothing to tell the user why.
 *   2. It defeats thumbnails. toThumbnailUrl only rewrites proxy paths, so a presigned URL slips
 *      past it and the full-resolution original is downloaded for a 40px avatar.
 *   3. It defeats caching. The objects carry no Cache-Control and the signature rotates every
 *      15 minutes, so the cache key keeps changing. The proxy serves a stable path with
 *      Cache-Control: public, max-age=3600.
 *
 * The rewrite is a pure string transformation, which is what makes it safe at this scale: a
 * screen full of simultaneously-expiring faces costs zero extra requests to fix, because we
 * never go back to ask for a fresh signature.
 *
 * See utils/photoUrl.ts for the parsing rules and the reasoning in full. api.ts exposes the
 * same behaviour as makeAbsolutePhotoUrl for callers that want a null rather than an empty
 * string; both delegate to resolveDisplayPhotoUrl so there is one set of rules.
 */
export const getAbsoluteImageUrl = (url?: string | null): string => {
  if (!url) {
    return '';
  }

  const baseUrl = getApiBaseUrl();
  const resolved = resolveDisplayPhotoUrl(url, (relative) =>
    baseUrl ? `${baseUrl}${relative}` : relative,
  );

  return resolved ?? '';
};
